import { PanelLeft, PanelRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { confirmModal, Modal, promptModal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useDocumentVariables } from '../api'
import { upsertField } from '../lib/templateHelpers'
import {
  addContentBlock,
  addPage,
  addSection,
  createFieldOnSection,
  findBlockIdByField,
  getFieldColumns,
  insertVariableAsField,
  moveBlock,
  moveSection,
  setFieldColumns,
} from '../lib/surumMutations'
import { useRemoveBlock } from '../hooks/useRemoveBlock'
import type { DocBlock, DocField, FieldColumns, SablonSurum } from '../types'
import { DocumentCanvas } from './DocumentCanvas'
import { FieldPropertiesPanel } from './FieldPropertiesPanel'
import { StructurePanel } from './StructurePanel'

type Props = {
  surum: SablonSurum
  onChange: (next: SablonSurum) => void
  className?: string
}

export function TemplateEditorWorkspace({ surum, onChange, className }: Props) {
  const { t } = useTranslation()
  const variables = useDocumentVariables().data ?? []
  const [pageId, setPageId] = useState(surum.pages[0]?.id ?? '')
  const [sectionId, setSectionId] = useState<string | null>(
    surum.sections.find((s) => s.pageId === surum.pages[0]?.id)?.id ?? null,
  )
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null)
  const [zoom, setZoom] = useState(0.9)
  const [leftOpen, setLeftOpen] = useState(false)
  const [rightOpen, setRightOpen] = useState(false)

  const activePageId = surum.pages.some((p) => p.id === pageId) ? pageId : surum.pages[0]?.id ?? ''
  const activeSectionId =
    sectionId && surum.sections.some((s) => s.id === sectionId)
      ? sectionId
      : surum.sections.find((s) => s.pageId === activePageId)?.id ?? null

  const requestRemove = useRemoveBlock(surum, onChange, (next) => {
    if (selectedFieldId && !next.fields.some((f) => f.id === selectedFieldId)) setSelectedFieldId(null)
  })

  const selectedField = useMemo(
    () => surum.fields.find((f) => f.id === selectedFieldId) ?? null,
    [surum.fields, selectedFieldId],
  )

  function ensureSection(): { base: SablonSurum; sec: string } | null {
    if (activeSectionId) return { base: surum, sec: activeSectionId }
    const base = addSection(surum, activePageId)
    const created = base.sections.find((s) => !surum.sections.some((o) => o.id === s.id))
    if (!created) return null
    setSectionId(created.id)
    return { base, sec: created.id }
  }

  function revealNewBlock(sec: string, next: SablonSurum) {
    const id = next.blocksBySection[sec]?.at(-1)?.id
    if (!id) return
    window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-block-id="${id}"]`)
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      el?.querySelector<HTMLElement>('[contenteditable="true"]')?.focus({ preventScroll: true })
    }, 60)
  }

  function handleAddField(partial: Pick<DocField, 'label' | 'type' | 'source' | 'variable' | 'key'>, columns: FieldColumns) {
    const target = ensureSection()
    if (!target) return
    const { surum: next, fieldId } = createFieldOnSection(target.base, target.sec, activePageId, partial, columns)
    onChange(next)
    setSelectedFieldId(fieldId)
    revealNewBlock(target.sec, next)
    toast.success(t('hr.belgeler.toasts.fieldAdded'))
  }

  function handleInsertVariable(key: string) {
    const def = variables.find((v) => v.key === key)
    const target = ensureSection()
    if (!target) return
    const { surum: next, fieldId } = insertVariableAsField(target.base, target.sec, activePageId, key, def?.label ?? key)
    onChange(next)
    setSelectedFieldId(fieldId)
    revealNewBlock(target.sec, next)
    toast.success(t('hr.belgeler.toasts.varInserted'))
  }

  const rightPanel = (
    <FieldPropertiesPanel
      field={selectedField}
      surum={surum}
      onChange={(field) => onChange(upsertField(surum, field))}
      onInsertVariable={handleInsertVariable}
      onAddField={handleAddField}
      onRemoveField={(fieldId) => {
        const blockId = findBlockIdByField(surum, fieldId)
        if (blockId) void requestRemove(blockId)
      }}
      fieldColumns={selectedFieldId ? getFieldColumns(surum, selectedFieldId) : 1}
      onFieldColumnsChange={(fieldId, columns) => onChange(setFieldColumns(surum, fieldId, columns))}
      onMarginsChange={(margins) => onChange({ ...surum, margins })}
      onLineHeightChange={(lineHeight) => onChange({ ...surum, lineHeight })}
      onLetterheadChange={(letterhead) => onChange({ ...surum, letterhead })}
    />
  )

  const leftPanel = (
    <StructurePanel
      surum={surum}
      activePageId={activePageId}
      activeSectionId={activeSectionId}
      onSelectPage={(id) => {
        setPageId(id)
        setSectionId(surum.sections.find((s) => s.pageId === id)?.id ?? null)
      }}
      onSelectSection={setSectionId}
      onAddPage={() => {
        const next = addPage(surum)
        onChange(next)
        const last = next.pages[next.pages.length - 1]
        if (last) setPageId(last.id)
      }}
      onAddSection={() => {
        const next = addSection(surum, activePageId)
        onChange(next)
        const created = next.sections[next.sections.length - 1]
        if (created) setSectionId(created.id)
      }}
      onRenameSection={(id) => {
        void (async () => {
          const current = surum.sections.find((s) => s.id === id)
          const title = await promptModal({
            title: t('hr.belgeler.structure.renamePrompt'),
            defaultValue: current?.title ?? '',
            confirmLabel: t('common.save'),
          })
          if (!title?.trim()) return
          onChange({
            ...surum,
            sections: surum.sections.map((s) => (s.id === id ? { ...s, title: title.trim() } : s)),
          })
        })()
      }}
      onDeleteSection={(id) => {
        if (surum.sections.length <= 1) {
          toast.error(t('hr.belgeler.structure.cannotDeleteLastSection'))
          return
        }
        void (async () => {
          const ok = await confirmModal({
            title: t('confirm.templateSectionDelete.title'),
            description: t('confirm.templateSectionDelete.body', { name: surum.sections.find((s) => s.id === id)?.title ?? '' }),
            confirmLabel: t('hr.belgeler.structure.delete'),
            destructive: true,
          })
          if (!ok) return
          const { [id]: _removed, ...rest } = surum.blocksBySection
          onChange({
            ...surum,
            sections: surum.sections.filter((s) => s.id !== id),
            fields: surum.fields.filter((f) => f.sectionId !== id),
            blocksBySection: rest,
          })
          if (sectionId === id) setSectionId(null)
        })()
      }}
      onRenamePage={(id) => {
        void (async () => {
          const current = surum.pages.find((p) => p.id === id)
          const title = await promptModal({
            title: t('hr.belgeler.structure.renamePrompt'),
            defaultValue: current?.title ?? '',
            confirmLabel: t('common.save'),
          })
          if (!title?.trim()) return
          onChange({
            ...surum,
            pages: surum.pages.map((p) => (p.id === id ? { ...p, title: title.trim() } : p)),
          })
        })()
      }}
      onDeletePage={(id) => {
        if (surum.pages.length <= 1) {
          toast.error(t('hr.belgeler.structure.cannotDeleteLastPage'))
          return
        }
        void (async () => {
          const ok = await confirmModal({
            title: t('confirm.templatePageDelete.title'),
            description: t('confirm.templatePageDelete.body', { name: surum.pages.find((p) => p.id === id)?.title ?? '' }),
            confirmLabel: t('hr.belgeler.structure.delete'),
            destructive: true,
          })
          if (!ok) return
          const sectionIds = surum.sections.filter((s) => s.pageId === id).map((s) => s.id)
          const blocksBySection = { ...surum.blocksBySection }
          for (const sid of sectionIds) delete blocksBySection[sid]
          onChange({
            ...surum,
            pages: surum.pages.filter((p) => p.id !== id),
            sections: surum.sections.filter((s) => s.pageId !== id),
            fields: surum.fields.filter((f) => f.pageId !== id),
            blocksBySection,
          })
        })()
      }}
      onMoveSection={(id, dir) => onChange(moveSection(surum, id, dir))}
      onAddContent={(kind) => {
        const target = ensureSection()
        if (!target) return
        const next = addContentBlock(target.base, target.sec, kind)
        if (next === target.base) return
        onChange(next)
        setLeftOpen(false)
        revealNewBlock(target.sec, next)
      }}
    />
  )

  return (
    <div
      className={cn(
        'flex min-h-[calc(100dvh-9rem)] flex-col overflow-hidden rounded-xl border border-border/70 bg-card',
        className,
      )}
    >
      <div className="flex items-center gap-2 border-b border-border/70 px-2 py-1.5 xl:hidden">
        <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => setLeftOpen(true)}>
          <PanelLeft />
          {t('hr.belgeler.structure.pages')}
        </Button>
        <Button type="button" variant="outline" size="sm" className="ml-auto h-8" onClick={() => setRightOpen(true)}>
          <PanelRight />
          {t('hr.belgeler.right.fields')}
        </Button>
      </div>

      <div className="grid min-h-0 flex-1 xl:grid-cols-[220px_minmax(0,1fr)_300px]">
        <aside className="hidden min-h-0 min-w-0 overflow-hidden border-r border-border/70 bg-muted/20 xl:block dark:bg-muted/10">{leftPanel}</aside>
        <DocumentCanvas
          surum={surum}
          pageId={activePageId}
          selectedFieldId={selectedFieldId}
          zoom={zoom}
          onZoomChange={setZoom}
          showToolbar
          onSelectField={setSelectedFieldId}
          onUpdateBlock={(secId, block: DocBlock) => {
            const blocks = (surum.blocksBySection[secId] ?? []).map((b) => (b.id === block.id ? block : b))
            onChange({ ...surum, blocksBySection: { ...surum.blocksBySection, [secId]: blocks } })
          }}
          onMoveBlock={(activeId, overId) => onChange(moveBlock(surum, activeId, overId))}
          onRemoveBlock={(blockId) => void requestRemove(blockId)}
          onUpdateSurumMeta={(patch) => onChange({ ...surum, ...patch })}
        />
        <aside className="hidden min-h-0 min-w-0 overflow-hidden border-l border-border/70 bg-muted/20 xl:block dark:bg-muted/10">{rightPanel}</aside>
      </div>

      <Modal open={leftOpen} onOpenChange={setLeftOpen} title={t('hr.belgeler.structure.pages')} variant="bare" size="sm">
        <div className="max-h-[84vh] overflow-y-auto scrollbar-none">{leftPanel}</div>
      </Modal>
      <Modal open={rightOpen} onOpenChange={setRightOpen} title={t('hr.belgeler.right.fields')} variant="bare" size="sm">
        <div className="max-h-[84vh] overflow-y-auto scrollbar-none">{rightPanel}</div>
      </Modal>
    </div>
  )
}
